import Vue from "vue";
import VueRouter from "vue-router";
import { getUserInfo } from "@/api/user";
import { shouldScan, isNeedScan } from "@/api/order";

Vue.use(VueRouter);

/** 需要强制关注公众号才能访问的页面 */
const FOLLOW_REQUIRED_PATHS = ["/scan-remind", "/billing-config", "/"];

const routes = [
  {
    path: "/",
    name: "Home",
    component: () =>
      import(/* webpackChunkName: "home" */ "@/views/Home/index.vue"),
    meta: {
      title: "首页",
      keepAlive: true,
      requireFollow: true,
      skipScanCheck: true,
    },
  },
  {
    path: "/mine",
    name: "Mine",
    component: () =>
      import(/* webpackChunkName: "mine" */ "@/views/Mine/index.vue"),
    meta: { title: "我的", keepAlive: true, skipScanCheck: true },
  },
  {
    path: "/auth",
    name: "Auth",
    component: () =>
      import(/* webpackChunkName: "auth" */ "@/views/Auth/index.vue"),
    meta: { title: "登录", public: true },
  },
  {
    path: "/balance",
    name: "Balance",
    component: () =>
      import(/* webpackChunkName: "balance" */ "@/views/Balance/index.vue"),
    meta: { title: "我的余额", skipScanCheck: true },
  },
  {
    path: "/deposit",
    name: "Deposit",
    component: () =>
      import(/* webpackChunkName: "deposit" */ "@/views/Deposit/index.vue"),
    meta: { title: "押金退款", skipScanCheck: true },
  },
  {
    path: "/orders",
    name: "Orders",
    component: () =>
      import(/* webpackChunkName: "orders" */ "@/views/Orders/index.vue"),
    meta: { title: "我的订单", skipScanCheck: true },
  },
  {
    path: "/order-detail/:orderNo",
    name: "OrderDetail",
    component: () =>
      import(
        /* webpackChunkName: "order-detail" */ "@/views/OrderDetail/index.vue"
      ),
    meta: { title: "订单详情", skipScanCheck: true },
  },
  {
    path: "/register",
    name: "Register",
    component: () =>
      import(/* webpackChunkName: "register" */ "@/views/Register/index.vue"),
    meta: { title: "注册" },
  },
  {
    path: "/change-password",
    name: "ChangePassword",
    component: () =>
      import(
        /* webpackChunkName: "change-password" */ "@/views/ChangePassword/index.vue"
      ),
    meta: { title: "修改密码", skipScanCheck: true },
  },
  {
    path: "/cooperation",
    name: "Cooperation",
    component: () =>
      import(
        /* webpackChunkName: "cooperation" */ "@/views/Cooperation/index.vue"
      ),
    meta: { title: "商业合作", public: true },
  },
  {
    path: "/notice",
    name: "Notice",
    component: () =>
      import(/* webpackChunkName: "notice" */ "@/views/Notice/index.vue"),
    meta: { title: "注意事项", public: true },
  },
  {
    path: "/agreement",
    name: "Agreement",
    component: () =>
      import(/* webpackChunkName: "agreement" */ "@/views/Agreement/index.vue"),
    meta: { title: "用户协议", public: true },
  },
  {
    path: "/about",
    name: "About",
    component: () =>
      import(/* webpackChunkName: "about" */ "@/views/About/index.vue"),
    meta: { title: "关于我们", public: true },
  },
  {
    path: "/help",
    name: "Help",
    component: () =>
      import(/* webpackChunkName: "help" */ "@/views/Help/index.vue"),
    meta: { title: "使用帮助", public: true, skipScanCheck: true },
  },
  {
    path: "/report-feedback",
    name: "ReportFeedback",
    component: () =>
      import(
        /* webpackChunkName: "report-feedback" */ "@/views/ReportFeedback/index.vue"
      ),
    meta: { title: "上报问题", skipScanCheck: true },
  },
  {
    path: "/scan-remind",
    name: "ScanRemind",
    component: () =>
      import(
        /* webpackChunkName: "scan-remind" */ "@/views/ScanRemind/index.vue"
      ),
    meta: { title: "扫码存包", requireFollow: true },
  },
  {
    path: "/billing-config",
    name: "BillingConfig",
    component: () =>
      import(
        /* webpackChunkName: "billing-config" */ "@/views/BillingConfig/index.vue"
      ),
    meta: { title: "计费信息", requireFollow: true },
  },
  {
    path: "/renew-billing-config",
    name: "RenewBillingConfig",
    component: () =>
      import(
        /* webpackChunkName: "renew-billing-config" */ "@/views/RenewBillingConfig/index.vue"
      ),
    meta: {
      title: "续费计费",
      requireFollow: true,
      /** 不经过扫码会话，仅订单续费 */
      skipScanCheck: true,
    },
  },
  {
    path: "/order-confirm",
    name: "OrderConfirm",
    component: () =>
      import(
        /* webpackChunkName: "order-confirm" */ "@/views/OrderConfirm/index.vue"
      ),
    meta: { title: "确认下单", requireFollow: true, skipScanCheck: true },
  },
  {
    path: "/extra-pay",
    name: "ExtraPay",
    component: () =>
      import(
        /* webpackChunkName: "extra-pay" */ "@/views/ExtraPay/index.vue"
      ),
    meta: { title: "补缴差价", skipScanCheck: true },
  },
  {
    path: "/follow-guide",
    name: "FollowGuide",
    component: () =>
      import(
        /* webpackChunkName: "follow-guide" */ "@/views/FollowGuide/index.vue"
      ),
    meta: { title: "关注公众号" },
  },
];

const router = new VueRouter({
  mode: "hash",
  base: process.env.BASE_URL,
  routes,
});

/**
 * 会话级关注状态缓存，避免每次路由跳转都请求后端。
 * 值为 true 表示本次会话已确认关注，用户点「已关注」后由 FollowGuide 页面重新请求并刷新。
 * 取关后下次打开页面（新会话）会重新检查。
 */
let subscribedInSession = false;

async function isUserSubscribed() {
  if (subscribedInSession) return true;
  try {
    const userInfo = await getUserInfo();
    const subscribed = userInfo && userInfo.isSubscribed !== false;
    if (subscribed) subscribedInSession = true;
    return subscribed;
  } catch (e) {
    return true;
  }
}

/** 判断目标路由是否需要强制关注 */
function needsFollowCheck(to) {
  if (to.meta.requireFollow) return true;
  return FOLLOW_REQUIRED_PATHS.includes(to.path);
}

/**
 * 无需扫码校验的路由：
 * - 首页：展示当前订单等信息
 * - 我的及从「我的」进入的页面（余额、押金、订单与详情、协议类入口等）
 */
function isScanExemptRoute(to) {
  if (to.meta && to.meta.skipScanCheck === true) return true;
  const p = to.path;
  if (p === "/" || p === "/mine") return true;
  if (p.startsWith("/order-detail/")) return true;
  const mineRelated = [
    "/balance",
    "/deposit",
    "/orders",
    "/order-confirm",
    "/change-password",
    "/cooperation",
    "/notice",
    "/agreement",
    "/about",
    "/help",
    "/report-feedback",
  ];
  return mineRelated.includes(p);
}

router.beforeEach(async (to, from, next) => {
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  const token = localStorage.getItem("token");

  if (process.env.NODE_ENV === "development" && to.query.debug === "true") {
    return next();
  }

  if (to.meta.public) {
    if (to.path === "/auth" && token) {
      const raw = to.query.redirect;
      if (typeof raw === "string" && raw.startsWith("/")) {
        if (raw === "/auth" || raw.startsWith("/auth?")) {
          return next({ path: "/", replace: true });
        }
        const resolved = router.resolve(raw);
        return next({ ...resolved.location, replace: true });
      }
      return next({ path: "/", replace: true });
    }
    return next();
  }

  if (!token) {
    return next({
      path: "/auth",
      query: { redirect: to.fullPath },
      replace: true,
    });
  }

  // 关注引导页本身不再检查，防止死循环
  if (to.path === "/follow-guide") {
    return next();
  }

  // ====== 入口流程：同一会话内首次进入受保护业务页时执行注册检查 + 扫码检查 ======
  if (!sessionStorage.getItem("entryFlowDone") && to.path !== "/register") {
    try {
      const userInfo = await getUserInfo();

      // Step 1: 新用户检查
      if (!userInfo || !userInfo.tenantId) {
        return next({ path: "/register", replace: true });
      }

      // Step 2: 关注检查（入口流程也走统一逻辑，顺便缓存状态）
      if (userInfo.isSubscribed === false) {
        return next({
          path: "/follow-guide",
          query: { redirect: to.fullPath },
          replace: true,
        });
      }
      subscribedInSession = true;

      // Step 3: 扫码检查（首页 / 我的及个人中心相关页无需扫码）
      if (!isScanExemptRoute(to)) {
        const scanResult = await shouldScan();
        if (isNeedScan(scanResult)) {
          sessionStorage.setItem("entryFlowDone", "1");
          return next({ path: "/scan-remind", replace: true });
        }
      }
      sessionStorage.setItem("entryFlowDone", "1");
    } catch (e) {
      if (e.message === "未授权") {
        return;
      }
      sessionStorage.setItem("entryFlowDone", "1");
    }
  }
  // ====== 关键业务页面：每次导航都检查关注状态（使用轻量接口 + 会话缓存） ======
  if (needsFollowCheck(to)) {
    const subscribed = await isUserSubscribed();
    if (!subscribed) {
      return next({
        path: "/follow-guide",
        query: { redirect: to.fullPath },
        replace: true,
      });
    }
  }
  // 访问计费页前强制检查是否已扫码
  if (to.path === "/billing-config") {
    try {
      const scanResult = await shouldScan();
      if (isNeedScan(scanResult)) {
        return next({ path: "/scan-remind", replace: true });
      }
    } catch (e) {}
  }

  next();
});
/** 供 FollowGuide 页面在确认关注后重置缓存 */
export function resetSubscribeCache() {
  subscribedInSession = false;
}

export default router;
